import type { Entity } from '@ms/yammer-data-support';

import type { Group, Thread, User } from '../types';
export const DefaultAppViewPropertyId = 'global';
export const NoAppViewPropertyValueOnServer = '';
export type AppViewPropertyKey =
  | 'group.actionTips.CMSubscriptionTip'
  | 'group.teachingBubbles.CMSubscriptionTeachingBubble'
  | 'group.teachingBubbles.GroupAvatarTeachingBubble'
  | 'group.teachingBubbles.GroupCoverPhotoTeachingBubble'
  | 'group.teachingBubbles.GroupDescriptionTeachingBubble'
  | 'networkQuestions.actionTips.onboarding'
  | 'user.storyline.outlookPublisherGuideOpen'
  | 'user.actionTips.FRETeachingBubbleStatus'
  | 'user.storyline.congratulationsTeachingBubbleStatus'
  | 'user.storyline.storylinePageHasBeenVisited'
  | 'user.storyline.suggestedMediaCoachingTip'
  | 'user.storyline.suggestedMentionsCoachingTip'
  | 'user.storyline.suggestedReactOrReplyCoachingTip'
  | 'user.storyline.mediaPostInitialPreview'
  | 'user.storyline.mediaPostTeachingBubble'
  | 'user.actionTips.roleBasedFRE'
  | 'user.actionTips.m365CopilotFRE'
  | 'user.actionTips.m365CopilotGroupChecklist'
  | 'user.actionTips.m365CopilotGroupSuggestedContent'
  | 'user.groupPreferences.filterPreference'
  | 'user.groupPreferences.sortPreference'
  | 'user.actionTips.communityAgentVerifyAnswer';
export type RecurrentDismissableAppViewPropertyTypes =
  | 'user.actionTips.roleBasedFRE'
  | 'user.actionTips.m365CopilotFRE'
  | 'user.actionTips.m365CopilotGroupChecklist'
  | 'user.actionTips.m365CopilotGroupSuggestedContent';
export interface DismissableAppViewProperty {
  readonly isDismissed: boolean;
}
// TODO: Removed in a separate PR along with other selectors in storyline
export type StorylineCoachingTipTypes =
  | 'user.storyline.suggestedMediaCoachingTip'
  | 'user.storyline.suggestedMentionsCoachingTip'
  | 'user.storyline.suggestedReactOrReplyCoachingTip';
export type StorylinePublisherCoachingTipTypes =
  | 'user.storyline.suggestedMediaCoachingTip'
  | 'user.storyline.suggestedMentionsCoachingTip';
export interface StorylinePageVisitedAppViewProperty {
  readonly visited: boolean;
}
export interface StorylineMediaPostInitialPreviewAppViewProperty {
  readonly seen: boolean;
}
export interface StorylineMediaPostTeachingBubbleAppViewProperty {
  readonly seen: boolean;
}
export interface OutlookPublisherGuideOpenAppViewProperty {
  readonly isOpen: boolean;
}
export interface SearchFRETeachingBubbleOpenAppViewProperty {
  readonly isOpen: boolean;
}
export interface StorylineCoachingTipAppViewProperty {
  readonly lastDisplayed?: number;
}
export interface AnalyticsTeachingBubbleAppViewProperty {
  readonly isVisited: boolean;
}
export type NetworkQuestionsOnboardingTipStatus = 'Expanded' | 'Collapsed' | 'Dismissed';
export interface NetworkQuestionsOnboardingTipAppViewProperty {
  readonly status: NetworkQuestionsOnboardingTipStatus;
  readonly shouldShowUpvoteBubble: boolean;
}
export interface StorylineThreadCoachingTipAppViewProperty extends StorylineCoachingTipAppViewProperty {
  readonly isDisplay?: boolean;
  readonly visibleThreadId?: Thread['id'];
  readonly visiblePageLocation?: string;
}
export type StorylineCongratulationsTeachingBubbleStatus = 'ReadyToShow' | 'Showing' | 'Hide';
export interface StorylineCongratulationsTeachingBubbleStatusAppViewProperty {
  readonly status: StorylineCongratulationsTeachingBubbleStatus;
}
export interface SubscriptionTipAppViewProperty {
  readonly isDismissed: boolean;
  readonly remindMeLater: boolean;
  readonly remindMeLaterClickedDate: number | null;
}
export interface SubscriptionTeachingBubbleAppViewProperty {
  readonly isDismissed: boolean;
}
export interface GroupTeachingBubbleAppViewProperty {
  readonly isDismissed: boolean;
  readonly date: number | null;
}
export interface RoleBasedFREAppViewProperty {
  readonly isDismissed: boolean;
  readonly dismissCount: number;
  readonly lastDismissed: number | null;
}
export interface GroupFeedFilterPreferenceAppViewProperty {
  readonly feedType: 'ALL' | 'UNSEEN';
}
export interface GroupFeedSortPreferenceAppViewProperty {
  readonly sortBy: 'CREATED_AT' | 'UPDATED_AT';
}
export interface CommunityAgentVerifyAnswerAppViewProperty {
  readonly completed: boolean;
}
export type AppViewPropertyJSON =
  | AnalyticsTeachingBubbleAppViewProperty
  | NetworkQuestionsOnboardingTipAppViewProperty
  | OutlookPublisherGuideOpenAppViewProperty
  | SearchFRETeachingBubbleOpenAppViewProperty
  | SubscriptionTipAppViewProperty
  | SubscriptionTeachingBubbleAppViewProperty
  | DismissableAppViewProperty
  | GroupTeachingBubbleAppViewProperty
  | StorylinePageVisitedAppViewProperty
  | StorylineCongratulationsTeachingBubbleStatusAppViewProperty
  | StorylineCoachingTipAppViewProperty
  | StorylineThreadCoachingTipAppViewProperty
  | StorylineMediaPostInitialPreviewAppViewProperty
  | StorylineMediaPostTeachingBubbleAppViewProperty
  | RoleBasedFREAppViewProperty
  | GroupFeedFilterPreferenceAppViewProperty
  | GroupFeedSortPreferenceAppViewProperty
  | CommunityAgentVerifyAnswerAppViewProperty;
export type AppViewPropertyValue = AppViewPropertyJSON | typeof NoAppViewPropertyValueOnServer | null | undefined;
export interface AppViewProperty extends Entity {
  readonly id: Group['id'] | User['id'] | typeof DefaultAppViewPropertyId;
  readonly key: AppViewPropertyKey;
  readonly value: AppViewPropertyValue;
}
export type AppViewPropertiesEntityMap = Record<string, AppViewProperty>;
export type AppViewProperties = Partial<Record<AppViewPropertyKey, AppViewPropertiesEntityMap>>;
export const defaultSubscriptionTipAppViewProperty: SubscriptionTipAppViewProperty = {
  isDismissed: false,
  remindMeLater: false,
  remindMeLaterClickedDate: null,
};
export const defaultSubscriptionTeachingBubbleProperty: SubscriptionTeachingBubbleAppViewProperty = {
  isDismissed: false,
};
export const defaultDismissableAppViewProperty: DismissableAppViewProperty = {
  isDismissed: false,
};
export const defaultGroupTeachingBubbleAppViewProperty: GroupTeachingBubbleAppViewProperty = {
  isDismissed: false,
  date: null,
};
export const defaultNetworkQuestionsOnboardingTipAppViewProperty: NetworkQuestionsOnboardingTipAppViewProperty = {
  status: 'Expanded',
  shouldShowUpvoteBubble: false,
};
const defaultStorylineMediaPostInitialPreviewProperty: StorylineMediaPostInitialPreviewAppViewProperty = {
  seen: false,
};
const defaultStorylineMediaPostTeachingBubbleProperty: StorylineMediaPostTeachingBubbleAppViewProperty = {
  seen: false,
};
const defaultStorylinePageVisitedProperty: StorylinePageVisitedAppViewProperty = { visited: true };
const defaultOutlookPublisherGuideOpen: OutlookPublisherGuideOpenAppViewProperty = { isOpen: true };
const defaultSearchFRETeachingBubbleOpen: SearchFRETeachingBubbleOpenAppViewProperty = { isOpen: true };
const defaultStorylineCongratulationsTeachingBubbleProperty: StorylineCongratulationsTeachingBubbleStatusAppViewProperty =
  {
    status: 'Hide',
  };
const defaultStorylineSuggestedCoachingTip: StorylineCoachingTipAppViewProperty = { lastDisplayed: 0 };
export const defaultRoleBasedFREAppViewProperty: RoleBasedFREAppViewProperty = {
  isDismissed: false,
  dismissCount: 0,
  lastDismissed: null,
};
export const defaultGroupFeedFilterPreferenceAppViewProperty: GroupFeedFilterPreferenceAppViewProperty = {
  feedType: 'ALL',
};
export const defaultGroupFeedSortPreferenceAppViewProperty: GroupFeedSortPreferenceAppViewProperty = {
  sortBy: 'CREATED_AT',
};
export const defaultCommunityAgentVerifyAnswerAppViewProperty: CommunityAgentVerifyAnswerAppViewProperty = {
  completed: false,
};
export const defaultAppViewProperties: Record<AppViewPropertyKey, AppViewPropertyJSON> = {
  ['group.actionTips.CMSubscriptionTip']: defaultSubscriptionTipAppViewProperty,
  ['group.teachingBubbles.CMSubscriptionTeachingBubble']: defaultSubscriptionTeachingBubbleProperty,
  ['group.teachingBubbles.GroupAvatarTeachingBubble']: defaultGroupTeachingBubbleAppViewProperty,
  ['group.teachingBubbles.GroupCoverPhotoTeachingBubble']: defaultGroupTeachingBubbleAppViewProperty,
  ['group.teachingBubbles.GroupDescriptionTeachingBubble']: defaultGroupTeachingBubbleAppViewProperty,
  ['networkQuestions.actionTips.onboarding']: defaultNetworkQuestionsOnboardingTipAppViewProperty,
  ['user.storyline.outlookPublisherGuideOpen']: defaultOutlookPublisherGuideOpen,
  ['user.actionTips.FRETeachingBubbleStatus']: defaultSearchFRETeachingBubbleOpen,
  ['user.storyline.storylinePageHasBeenVisited']: defaultStorylinePageVisitedProperty,
  ['user.storyline.congratulationsTeachingBubbleStatus']: defaultStorylineCongratulationsTeachingBubbleProperty,
  ['user.storyline.mediaPostInitialPreview']: defaultStorylineMediaPostInitialPreviewProperty,
  ['user.storyline.mediaPostTeachingBubble']: defaultStorylineMediaPostTeachingBubbleProperty,
  ['user.storyline.suggestedMediaCoachingTip']: defaultStorylineSuggestedCoachingTip,
  ['user.storyline.suggestedMentionsCoachingTip']: defaultStorylineSuggestedCoachingTip,
  ['user.storyline.suggestedReactOrReplyCoachingTip']: defaultStorylineSuggestedCoachingTip,
  ['user.actionTips.roleBasedFRE']: defaultRoleBasedFREAppViewProperty,
  ['user.actionTips.m365CopilotFRE']: defaultRoleBasedFREAppViewProperty,
  ['user.actionTips.m365CopilotGroupChecklist']: defaultDismissableAppViewProperty,
  ['user.actionTips.m365CopilotGroupSuggestedContent']: defaultDismissableAppViewProperty,
  ['user.groupPreferences.filterPreference']: defaultGroupFeedFilterPreferenceAppViewProperty,
  ['user.groupPreferences.sortPreference']: defaultGroupFeedSortPreferenceAppViewProperty,
  ['user.actionTips.communityAgentVerifyAnswer']: defaultCommunityAgentVerifyAnswerAppViewProperty,
};
